import { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import ReleaseComparisonView from './ReleaseComparisonView'
import ReportsConfigView from './ReportsConfigView'

// ─────────────────────────────────────────────────────────────────────────
// ReleaseManagement — standalone page (ADD_Forecast_Architecture.md SS17,
// per explicit agreement during Cost Control closeout).
//
// ARCHITECTURAL POSITION: a Project Forecast Release is a project-wide
// approved snapshot, consumed by multiple modules (Cost Control today;
// Executive Report, Dashboard, Cash Flow later). This page is therefore
// NOT a Cost Control tab or modal — it owns the Release workflow
// independently, and any consumer module opens a Release FROM here via
// onOpenInCostControl (or an equivalent callback for a future consumer),
// never the reverse.
//
// SCOPE (explicitly agreed):
//   - Release History is the PRIMARY content — most of a user's time here
//     is spent browsing, not creating.
//   - Create Release is a SECONDARY action (a dialog opened by a button),
//     never dominating the page.
//   - No Edit. No Delete. A Release is immutable — corrections are always
//     a NEW Release that supersedes the previous one for the same period.
//   - "Open in Cost Control" is the only cross-module action implemented
//     today; the callback prop name is generic (onOpenInCostControl) so
//     a future second consumer can be wired the same way without
//     reshaping this page.
export default function ReleaseManagement({ projectId, onOpenInCostControl }) {
  const { can } = useProjectAuth(projectId)
  const [activeTab, setActiveTab] = useState('releases') // 'releases' | 'compare' | 'reports'
  const [releases, setReleases] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [viewingRelease, setViewingRelease] = useState(null)

  const loadReleases = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    // Consumes the shared project_forecast_release_summary view
    // (ADD_Forecast_Architecture.md SS17/SS19 — SSOT for Release summary
    // data) — the same view CostControl.jsx's picker already uses, so
    // this page and Cost Control can never disagree about what a
    // Release's composition is.
    const { data } = await supabase
      .from('project_forecast_release_summary')
      .select('release_id, project_id, forecast_date, release_number, direct_snapshot_id, direct_revision_number, indirect_snapshot_id, indirect_revision_number, notes, created_at, is_current')
      .eq('project_id', projectId)
      .order('forecast_date', { ascending: false })
      .order('release_number', { ascending: false })
    setReleases(data || [])
    setLoading(false)
  }, [projectId])

  useEffect(() => { loadReleases() }, [loadReleases])

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100%', background:'#F9FAFB' }}>
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'18px 24px 14px', display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16, flexShrink:0 }}>
        <div>
          <h1 style={{ fontSize:20, fontWeight:800, color:'#0F1017', letterSpacing:'-0.02em', lineHeight:1.2, marginBottom:3 }}>
            Project Forecast Releases
          </h1>
          <p style={{ fontSize:11.5, color:'#6B7280', marginTop:3, lineHeight:1.4, maxWidth:640 }}>
            The approved project financial state for each period — Direct and Indirect Revisions paired as one release. Releases are immutable; corrections are made by creating a new Release.
          </p>
        </div>
        {activeTab === 'releases' && can('Release.Create') && (
          <button onClick={() => setShowCreateModal(true)}
            style={{ padding:'8px 16px', background:'#1A7A4A', color:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:'pointer', flexShrink:0 }}>
            + Create Release
          </button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'0 24px', display:'flex', gap:4, flexShrink:0 }}>
        {[
          { key: 'releases', label: 'Releases' },
          { key: 'compare', label: 'Compare Releases' },
          { key: 'reports', label: 'Reports' },
        ].map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ padding:'10px 14px', border:'none', borderBottom: activeTab===t.key ? '2px solid #1A7A4A' : '2px solid transparent',
              background:'none', color: activeTab===t.key ? '#0F1017' : '#6B7280', fontSize:13, fontWeight: activeTab===t.key ? 700 : 500, cursor:'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'compare' ? (
        <ReleaseComparisonView projectId={projectId} />
      ) : activeTab === 'reports' ? (
        <ReportsConfigView projectId={projectId} />
      ) : (
      <>
      <div style={{ flex:1, overflow:'auto', padding:'20px 24px' }}>
        {loading ? (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:60, color:'#6B7280', fontSize:13 }}>
            Loading releases…
          </div>
        ) : releases.length === 0 ? (
          <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:60, gap:8 }}>
            <div style={{ fontSize:32 }}>—</div>
            <p style={{ fontSize:14, fontWeight:600, color:'#374151' }}>No releases exist yet for this project.</p>
            <p style={{ fontSize:12, color:'#9CA3AF' }}>Create the first release once a Direct and/or Indirect Forecast has been committed.</p>
          </div>
        ) : (
          <div style={{ border:'1px solid #E5E7EB', borderRadius:12, background:'#fff', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12.5 }}>
              <thead>
                <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                  {['Release', 'Period', 'Direct Revision', 'Indirect Revision', 'Created', 'Notes', ''].map(h => (
                    <th key={h} style={{ padding:'10px 14px', textAlign:'left', fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', color:'#6B7280', whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {releases.map(r => (
                  <tr key={r.release_id} style={{ borderBottom:'1px solid #F3F4F6' }}>
                    <td style={{ padding:'10px 14px', fontWeight:700, color:'#0F1017' }}>
                      #{r.release_number}
                      {r.is_current && (
                        <span style={{ marginLeft:8, padding:'2px 8px', background:'#D1FAE5', color:'#065F46', borderRadius:999, fontSize:10, fontWeight:700 }}>Current</span>
                      )}
                    </td>
                    <td style={{ padding:'10px 14px', color:'#374151' }}>
                      {new Date(r.forecast_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})}
                    </td>
                    <td style={{ padding:'10px 14px', color: r.direct_snapshot_id ? '#374151' : '#D1D5DB' }}>
                      {r.direct_snapshot_id ? `Rev.${r.direct_revision_number}` : 'None'}
                    </td>
                    <td style={{ padding:'10px 14px', color: r.indirect_snapshot_id ? '#374151' : '#D1D5DB' }}>
                      {r.indirect_snapshot_id ? `Rev.${r.indirect_revision_number}` : 'None'}
                    </td>
                    <td style={{ padding:'10px 14px', color:'#6B7280', whiteSpace:'nowrap' }}>
                      {new Date(r.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                    </td>
                    <td style={{ padding:'10px 14px', color:'#6B7280', maxWidth:220, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={r.notes || ''}>
                      {r.notes || '—'}
                    </td>
                    <td style={{ padding:'10px 14px', whiteSpace:'nowrap' }}>
                      <button onClick={() => setViewingRelease(r)}
                        style={{ padding:'5px 10px', border:'1px solid #E5E7EB', borderRadius:6, background:'#fff', color:'#374151', fontSize:11.5, fontWeight:500, cursor:'pointer', marginRight:6 }}>
                        View
                      </button>
                      <button onClick={() => onOpenInCostControl?.(r.release_id)}
                        style={{ padding:'5px 10px', border:'1px solid #1A7A4A', borderRadius:6, background:'#fff', color:'#1A7A4A', fontSize:11.5, fontWeight:600, cursor:'pointer' }}>
                        Open in Cost Control
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && (
        <CreateReleaseModal
          projectId={projectId}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => { setShowCreateModal(false); loadReleases() }}
        />
      )}

      {viewingRelease && (
        <ViewReleaseModal
          release={viewingRelease}
          onClose={() => setViewingRelease(null)}
          onOpenInCostControl={() => { onOpenInCostControl?.(viewingRelease.release_id); setViewingRelease(null) }}
        />
      )}
      </>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// CreateReleaseModal — the "explicit user action" surface for
// create_project_forecast_release() (ADD SS17.3). Defaults to the latest
// committed Direct and Indirect revisions (explicitly agreed UX), but
// either can be changed before the explicit "Create Release" confirmation.
function CreateReleaseModal({ projectId, onClose, onCreated }) {
  const [directRevisions, setDirectRevisions] = useState([])
  const [indirectRevisions, setIndirectRevisions] = useState([])
  const [selectedDirectId, setSelectedDirectId] = useState(null)
  const [selectedIndirectId, setSelectedIndirectId] = useState(null)
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState(null)

  // forecast_date is DERIVED from whichever Revisions are currently
  // selected — never held as separate state. This is a deliberate fix
  // for a prior bug where forecast_date was computed once when the
  // Modal opened (from the initially-defaulted latest Revisions) and
  // never recomputed when the user changed their selection afterward,
  // causing create_project_forecast_release to reject the mismatch.
  // Recomputing this on every render from the current selection makes
  // that class of bug structurally impossible.
  const forecastDate = useMemo(() => {
    const selectedDirect = directRevisions.find(r => r.id === selectedDirectId)
    const selectedIndirect = indirectRevisions.find(r => r.id === selectedIndirectId)
    return selectedDirect?.forecast_date || selectedIndirect?.forecast_date || null
  }, [selectedDirectId, selectedIndirectId, directRevisions, indirectRevisions])

  useEffect(() => {
    if (!projectId) return
    ;(async () => {
      setLoading(true)
      const [{ data: directData }, { data: indirectData }] = await Promise.all([
        supabase.from('forecast_snapshots').select('id, forecast_date, revision_number')
          .eq('project_id', projectId).eq('source', 'direct').eq('status', 'committed')
          .order('revision_number', { ascending: false }),
        supabase.from('forecast_snapshots').select('id, forecast_date, revision_number')
          .eq('project_id', projectId).eq('source', 'indirect').eq('status', 'committed')
          .order('revision_number', { ascending: false }),
      ])
      setDirectRevisions(directData || [])
      setIndirectRevisions(indirectData || [])
      // Default: latest committed Direct and latest committed Indirect
      // (explicitly agreed UX) — either can be changed below before
      // creating. forecastDate above will automatically recompute from
      // whatever is selected, including these defaults.
      const latestDirect = directData?.[0]
      const latestIndirect = indirectData?.[0]
      setSelectedDirectId(latestDirect?.id || null)
      setSelectedIndirectId(latestIndirect?.id || null)
      setLoading(false)
    })()
  }, [projectId])

  async function handleCreate() {
    if (!selectedDirectId && !selectedIndirectId) {
      setError('At least one of Direct or Indirect must be selected.')
      return
    }
    setCreating(true)
    setError(null)
    try {
      const { error: rpcError } = await supabase.rpc('create_project_forecast_release', {
        p_project_id: projectId,
        p_forecast_date: forecastDate,
        p_direct_snapshot_id: selectedDirectId,
        p_indirect_snapshot_id: selectedIndirectId,
        p_notes: notes || null,
      })
      if (rpcError) throw rpcError
      onCreated()
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setCreating(false)
    }
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:480, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize:16, fontWeight:800, color:'#0F1017', marginBottom:4 }}>Create Project Forecast Release</h2>
        <p style={{ fontSize:11.5, color:'#6B7280', marginBottom:18, lineHeight:1.5 }}>
          Pairs one committed Direct Revision with one committed Indirect Revision as the approved project forecast for this period. This action is permanent — the Release cannot be edited or deleted afterward.
        </p>

        {loading ? (
          <p style={{ fontSize:13, color:'#6B7280' }}>Loading revisions…</p>
        ) : (
          <>
            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Direct Revision</label>
            <select value={selectedDirectId || ''} onChange={e => setSelectedDirectId(e.target.value || null)}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, marginBottom:14 }}>
              <option value="">None</option>
              {directRevisions.map(r => (
                <option key={r.id} value={r.id}>
                  Rev.{r.revision_number} — {new Date(r.forecast_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})}
                </option>
              ))}
            </select>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Indirect Revision</label>
            <select value={selectedIndirectId || ''} onChange={e => setSelectedIndirectId(e.target.value || null)}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, marginBottom:14 }}>
              <option value="">None</option>
              {indirectRevisions.map(r => (
                <option key={r.id} value={r.id}>
                  Rev.{r.revision_number} — {new Date(r.forecast_date).toLocaleDateString('en-US',{month:'short',year:'numeric'})}
                </option>
              ))}
            </select>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'.05em', marginBottom:6 }}>Notes (optional)</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              style={{ width:'100%', padding:'8px 10px', border:'1px solid #E5E7EB', borderRadius:8, fontSize:13, marginBottom:14, resize:'vertical', fontFamily:'inherit' }}
              placeholder="e.g. August 2026 monthly forecast approval"/>

            {error && <p style={{ fontSize:12, color:'#DC2626', marginBottom:12 }}>{error}</p>}
          </>
        )}

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:6 }}>
          <button onClick={onClose} disabled={creating}
            style={{ padding:'8px 16px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', color:'#6B7280', fontSize:13, fontWeight:500, cursor:'pointer' }}>
            Cancel
          </button>
          <button onClick={handleCreate} disabled={creating || loading}
            style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#1A7A4A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', opacity: (creating||loading) ? 0.6 : 1 }}>
            {creating ? 'Creating…' : 'Create Release'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────
// ViewReleaseModal — read-only detail view. No Edit/Delete controls exist
// anywhere in this component, by design (explicitly agreed).
function ViewReleaseModal({ release, onClose, onOpenInCostControl }) {
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:12, padding:24, width:440, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}
        onClick={e => e.stopPropagation()}>
        <h2 style={{ fontSize:16, fontWeight:800, color:'#0F1017', marginBottom:2 }}>
          Release #{release.release_number}
          {release.is_current && <span style={{ marginLeft:8, padding:'2px 8px', background:'#D1FAE5', color:'#065F46', borderRadius:999, fontSize:10, fontWeight:700 }}>Current</span>}
        </h2>
        <p style={{ fontSize:11.5, color:'#9CA3AF', marginBottom:18 }}>
          {new Date(release.forecast_date).toLocaleDateString('en-US',{month:'long',year:'numeric'})} · 🔒 Immutable — approved historical state
        </p>

        <div style={{ display:'grid', gridTemplateColumns:'auto 1fr', rowGap:10, columnGap:16, fontSize:13, marginBottom:18 }}>
          <span style={{ color:'#6B7280', fontWeight:600 }}>Direct Revision</span>
          <span style={{ color: release.direct_snapshot_id ? '#0F1017' : '#D1D5DB' }}>
            {release.direct_snapshot_id ? `Rev.${release.direct_revision_number}` : 'None attached'}
          </span>
          <span style={{ color:'#6B7280', fontWeight:600 }}>Indirect Revision</span>
          <span style={{ color: release.indirect_snapshot_id ? '#0F1017' : '#D1D5DB' }}>
            {release.indirect_snapshot_id ? `Rev.${release.indirect_revision_number}` : 'None attached'}
          </span>
          <span style={{ color:'#6B7280', fontWeight:600 }}>Created</span>
          <span style={{ color:'#0F1017' }}>{new Date(release.created_at).toLocaleString('en-US')}</span>
          {release.notes && (<>
            <span style={{ color:'#6B7280', fontWeight:600 }}>Notes</span>
            <span style={{ color:'#0F1017' }}>{release.notes}</span>
          </>)}
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={onClose}
            style={{ padding:'8px 16px', border:'1px solid #E5E7EB', borderRadius:8, background:'#fff', color:'#6B7280', fontSize:13, fontWeight:500, cursor:'pointer' }}>
            Close
          </button>
          <button onClick={onOpenInCostControl}
            style={{ padding:'8px 16px', border:'none', borderRadius:8, background:'#1A7A4A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
            Open in Cost Control
          </button>
        </div>
      </div>
    </div>
  )
}
